import { TranslateFn } from 'src/localization/translate'

/**
 * The ACH acknowledgement copy + version, shared by the add-vendor flow
 * (VendorFlyout) and the Payment Information tab's re-add flow (EX-40772). Kept
 * in its own leaf module so both the flyout and `useVendorPaymentMethod` can
 * import it without closing an import cycle (the flyout imports the hook, and
 * the hook needs this constant). `version` is the copy version sent on confirm;
 * `heading`/`description` are the visible acknowledgement copy, rendered as two
 * lines in that order.
 */
export const VENDOR_ACKNOWLEDGEMENT = {
  version: '2026-07-28',
  heading: 'I acknowledge that...',
  description:
    'These ACH account details are accurate and authorized for use when paying this vendor.',
} as const

/**
 * Shape shared by every vendor acknowledgement constant — the ACH one above and
 * the details one in `useVendorDetailsForm`. Each surface owns its own copy and
 * its own version, so the helper below takes the constant rather than reading a
 * single module-level one.
 */
export interface VendorAcknowledgementCopy {
  version: string
  heading: string
  description: string
}

/**
 * The wording to record as `acknowledgementCopyText` (EX-40809) — `heading` and
 * `description` joined in render order, because the user reads both lines and
 * the heading is the sentence that makes it an acknowledgement at all.
 *
 * Translated, not the raw constant: the copy renders through `translate()`, so
 * on a non-English locale the words on screen are not the English source. The
 * column is evidence of what the user actually read, and extend-api's schema
 * asks for the string that was rendered — recording the English while a fr-CA
 * user agreed to the French is evidence of the wrong sentence.
 *
 * Every call site composes through here instead of re-typing the words. A
 * second copy of the copy can drift from the rendered one, which is the exact
 * failure `vendor_acknowledgements.copy_text` exists to prevent. Plain text by
 * construction: the record is the words, not the styling.
 *
 * The constants stay the English source strings — they are the `en-US.json`
 * keys, and the same keys appear as literals in the markup, which is what keeps
 * `checkLocalization` able to validate them.
 */
export function acknowledgementCopyText(
  translate: TranslateFn,
  acknowledgement: VendorAcknowledgementCopy,
): string {
  return `${translate(acknowledgement.heading)} ${translate(
    acknowledgement.description,
  )}`
}
